import React, { useState, useEffect } from 'react';
import WidgetWrapper from './WidgetWrapper';
import { customers, entries } from '../../services/api';

const StatsWidget = ({ darkMode = false, onError }) => {
  const [windowWidth, setWindowWidth] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 800;

  const [summary, setSummary] = useState({
    totalCustomers: 0,
    totalEntries: 0,
    totalRevenue: 0,
    unbilledAmount: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const now = new Date();
        const year = now.getFullYear();
        const startDate = `${year}-01-01`;
        const endDate = `${year}-12-31`;

        const [customersRes, entriesRes] = await Promise.all([
          customers.list({ limit: 1 }),
          entries.list({ startDate, endDate, limit: 1000 })
        ]);

        const allEntries = entriesRes.data?.entries || entriesRes.data || [];
        const totalRevenue = allEntries.reduce((sum, e) => sum + (e.totalCost || 0), 0);
        const unbilledAmount = allEntries
          .filter(e => e.status === 'unbilled')
          .reduce((sum, e) => sum + (e.totalCost || 0), 0);

        const custData = customersRes.data?.customers || customersRes.data || [];
        const totalCust = customersRes.data?.total || custData.length || 0;

        setSummary({
          totalCustomers: totalCust,
          totalEntries: allEntries.length,
          totalRevenue,
          unbilledAmount
        });
      } catch (error) {
        console.error('StatsWidget error:', error);
        onError?.('StatsWidget', error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [onError]);

  const subTextColor = darkMode ? '#9ca3af' : '#6b7280';
  const textColor = darkMode ? '#f3f4f6' : '#111827';

  const stats = [
    { label: 'Total Customers', value: summary.totalCustomers },
    { label: 'Time Entries', value: summary.totalEntries },
    { label: 'Total Revenue', value: `$${summary.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, class: 'success' },
    { label: 'Unbilled', value: `$${summary.unbilledAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, class: 'warning' }
  ];

  return (
    <WidgetWrapper title="📊 Overview" loading={loading} darkMode={darkMode}>
      <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: isMobile ? 8 : 16 }}>
        {stats.map((stat, i) => (
          <div key={i} className="stat-card" style={{
            background: darkMode ? '#1f2937' : '#fff',
            padding: isMobile ? 8 : 16,
            borderRadius: 8
          }}>
            <div className="stat-label" style={{ color: subTextColor, fontSize: 12 }}>{stat.label}</div>
            <div className={`stat-value ${stat.class || ''}`} style={{
              color: stat.class === 'success' ? '#22c55e' : stat.class === 'warning' ? '#f59e0b' : textColor,
              fontSize: 20,
              fontWeight: 'bold'
            }}>{stat.value}</div>
          </div>
        ))}
      </div>
    </WidgetWrapper>
  );
};

StatsWidget.widgetId = 'stats';
StatsWidget.title = '📊 Overview';
StatsWidget.defaultSize = { cols: 12 };
StatsWidget.category = 'business';

export default StatsWidget;